/**
 * carteira-store — C3 (rodada V2-COMERCIAL-AJUDA-UX-QA).
 *
 * Store do offboarding de carteira comercial: quando um colaborador do
 * departamento comercial é INATIVADO, o admin decide o destino da carteira
 * dele (leads, propostas e clientes) antes de concluir. NADA é apagado —
 * o nome permanece nos históricos.
 *
 * Contrato backend (construído em paralelo nesta rodada):
 *   GET  /api/v1/crm/carteira/:userId              → CarteiraResumo
 *   POST /api/v1/crm/carteira/:userId/offboarding  → OffboardingResultado
 *     (o POST JÁ inativa o usuário e derruba a sessão — não precisa de PUT depois)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '../api';

// ─── Tipos do contrato ──────────────────────────────────────────────────

export interface CarteiraContagens {
  leads: number;
  cards: number;
  clientes: number;
}

export interface CarteiraResumo {
  usuarioNome: string;
  carteira: CarteiraContagens;
}

export type OffboardingModo = 'transferir_tudo' | 'pool_tudo' | 'rodizio' | 'misto';

export type AcaoLeads = 'transferir' | 'pool' | 'rodizio';
export type AcaoCards = 'transferir' | 'rodizio' | 'liberar';
export type AcaoClientes = 'transferir' | 'manter' | 'sem_dono';

export interface OffboardingPorCategoria {
  leads?: { acao: AcaoLeads; paraId?: string };
  cards?: { acao: AcaoCards; paraId?: string };
  clientes?: { acao: AcaoClientes; paraId?: string };
}

export interface OffboardingBody {
  modo: OffboardingModo;
  paraId?: string;
  porCategoria?: OffboardingPorCategoria;
}

export interface OffboardingResultado {
  ok: true;
  movidos: CarteiraContagens;
  usuarioInativado: true;
}

// ─── Queries / mutations ────────────────────────────────────────────────

/**
 * Resumo da carteira de um usuário (contagens + nome). Só dispara quando
 * `enabled` (wizard aberto) — não busca à toa em toda edição de usuário.
 */
export function useCarteiraResumo(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['v1', 'crm', 'carteira', userId ?? ''] as const,
    queryFn: () => api.get<CarteiraResumo>(`/api/v1/crm/carteira/${encodeURIComponent(userId!)}`),
    enabled: enabled && !!userId && hasToken(),
    staleTime: 0,
  });
}

/**
 * Executa o offboarding da carteira. O backend move a carteira conforme o
 * modo escolhido, INATIVA o usuário e derruba a sessão dele — o caller não
 * precisa chamar o PUT de `ativo` depois.
 */
export function useOffboardingCarteira(userId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OffboardingBody) =>
      api.post<OffboardingResultado>(
        `/api/v1/crm/carteira/${encodeURIComponent(userId ?? '')}/offboarding`,
        body,
      ),
    onSuccess: () => {
      // Usuário sumiu dos dropdowns de responsável + carteira mudou de mãos.
      void qc.invalidateQueries({ queryKey: ['v1', 'users'] });
      void qc.invalidateQueries({ queryKey: ['v1', 'crm'] });
    },
  });
}

// ─── Helpers puros (testáveis) ──────────────────────────────────────────

/** Estado que o wizard coleta antes de montar o POST. */
export interface EscolhaOffboarding {
  modo: OffboardingModo;
  /** Destino quando modo = transferir_tudo. */
  paraId?: string;
  /** Escolhas por categoria quando modo = misto. */
  leads?: { acao: AcaoLeads; paraId?: string };
  cards?: { acao: AcaoCards; paraId?: string };
  clientes?: { acao: AcaoClientes; paraId?: string };
}

/**
 * Monta o body do POST /offboarding a partir das escolhas do wizard.
 * Regras: `paraId` só vai quando faz sentido (transferir); no modo misto,
 * `porCategoria` leva as três categorias com os respectivos destinos.
 */
export function montarBodyOffboarding(escolha: EscolhaOffboarding): OffboardingBody {
  if (escolha.modo === 'transferir_tudo') {
    return { modo: 'transferir_tudo', paraId: escolha.paraId };
  }
  if (escolha.modo === 'misto') {
    const porCategoria: OffboardingPorCategoria = {};
    if (escolha.leads) {
      porCategoria.leads =
        escolha.leads.acao === 'transferir'
          ? { acao: 'transferir', paraId: escolha.leads.paraId }
          : { acao: escolha.leads.acao };
    }
    if (escolha.cards) {
      porCategoria.cards =
        escolha.cards.acao === 'transferir'
          ? { acao: 'transferir', paraId: escolha.cards.paraId }
          : { acao: escolha.cards.acao };
    }
    if (escolha.clientes) {
      porCategoria.clientes =
        escolha.clientes.acao === 'transferir'
          ? { acao: 'transferir', paraId: escolha.clientes.paraId }
          : { acao: escolha.clientes.acao };
    }
    return { modo: 'misto', porCategoria };
  }
  return { modo: escolha.modo };
}

/** Valida se as escolhas estão completas pra habilitar o "Concluir". */
export function escolhaCompleta(escolha: EscolhaOffboarding): boolean {
  if (escolha.modo === 'transferir_tudo') return !!escolha.paraId;
  if (escolha.modo === 'misto') {
    if (!escolha.leads || !escolha.cards || !escolha.clientes) return false;
    if (escolha.leads.acao === 'transferir' && !escolha.leads.paraId) return false;
    if (escolha.cards.acao === 'transferir' && !escolha.cards.paraId) return false;
    if (escolha.clientes.acao === 'transferir' && !escolha.clientes.paraId) return false;
    return true;
  }
  return true;
}

function plural(n: number, singular: string, pluralStr: string): string {
  return `${n} ${n === 1 ? singular : pluralStr}`;
}

const fraseLeads = (n: number, acao: AcaoLeads, nomeDestino?: string): string => {
  const qtd = plural(n, 'lead', 'leads');
  if (acao === 'transferir') return `${qtd} para ${nomeDestino ?? 'o vendedor escolhido'}`;
  if (acao === 'pool') return `${qtd} ${n === 1 ? 'vai' : 'vão'} pro pool (sem responsável, qualquer vendedor pode pegar)`;
  return `${qtd} ${n === 1 ? 'distribuído' : 'distribuídos'} por rodízio entre os vendedores ativos`;
};

const fraseCards = (n: number, acao: AcaoCards, nomeDestino?: string): string => {
  const qtd = plural(n, 'proposta', 'propostas');
  if (acao === 'transferir') return `${qtd} para ${nomeDestino ?? 'o vendedor escolhido'}`;
  if (acao === 'rodizio') return `${qtd} ${n === 1 ? 'distribuída' : 'distribuídas'} por rodízio entre os vendedores ativos`;
  return `${qtd} ${n === 1 ? 'fica' : 'ficam'} sem responsável`;
};

const fraseClientes = (n: number, acao: AcaoClientes, nomeDestino?: string): string => {
  const qtd = plural(n, 'cliente', 'clientes');
  if (acao === 'transferir') return `${qtd} para ${nomeDestino ?? 'o vendedor escolhido'}`;
  if (acao === 'manter') return `${qtd} ${n === 1 ? 'mantido como está' : 'mantidos como estão'}`;
  return `${qtd} ${n === 1 ? 'fica' : 'ficam'} sem vendedor`;
};

/**
 * Frase de confirmação em pt-BR simples do que vai acontecer com a carteira.
 * Ex.: "15 leads vão pro pool..., 4 propostas para Igor Martins e 8 clientes
 * mantidos como estão."
 *
 * `nomePorId` resolve o NOME do vendedor destino (regra do projeto: nomes,
 * nunca IDs, na frente do usuário).
 */
export function montarFraseResumo(
  contagens: CarteiraContagens,
  escolha: EscolhaOffboarding,
  nomePorId: (id: string | undefined) => string | undefined,
): string {
  const { leads, cards, clientes } = contagens;

  if (escolha.modo === 'transferir_tudo') {
    const nome = nomePorId(escolha.paraId) ?? 'o vendedor escolhido';
    return `${plural(leads, 'lead', 'leads')}, ${plural(cards, 'proposta', 'propostas')} e ${plural(clientes, 'cliente', 'clientes')} vão para ${nome}.`;
  }
  if (escolha.modo === 'pool_tudo') {
    return `${plural(leads, 'lead', 'leads')}, ${plural(cards, 'proposta', 'propostas')} e ${plural(clientes, 'cliente', 'clientes')} ficam livres no pool, sem responsável — qualquer vendedor pode assumir.`;
  }
  if (escolha.modo === 'rodizio') {
    return `${plural(leads, 'lead', 'leads')}, ${plural(cards, 'proposta', 'propostas')} e ${plural(clientes, 'cliente', 'clientes')} serão distribuídos por rodízio, em partes iguais, entre os vendedores ativos.`;
  }

  // misto
  const partes: string[] = [];
  if (escolha.leads) partes.push(fraseLeads(leads, escolha.leads.acao, nomePorId(escolha.leads.paraId)));
  if (escolha.cards) partes.push(fraseCards(cards, escolha.cards.acao, nomePorId(escolha.cards.paraId)));
  if (escolha.clientes) partes.push(fraseClientes(clientes, escolha.clientes.acao, nomePorId(escolha.clientes.paraId)));
  if (partes.length === 0) return '';
  if (partes.length === 1) return `${partes[0]}.`;
  return `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}.`;
}

/** Frase curta do toast de sucesso a partir do `movidos` do backend. */
export function montarFraseMovidos(movidos: CarteiraContagens): string {
  return `Carteira encaminhada: ${plural(movidos.leads, 'lead', 'leads')}, ${plural(movidos.cards, 'proposta', 'propostas')} e ${plural(movidos.clientes, 'cliente', 'clientes')}.`;
}
